import React, { FC } from 'react';
import { compact, isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useSearchFilter } from 'providers';
import { GroupAvatar, UserAvatar } from 'components';
import { PostShareStatus, PublicUserProfileFragment, UserFeedFollowGroupFragment } from 'api';

import { initialPostFilter, useSearchESContext } from '../SearchESContext';
import { FilterChip } from './Filter';
import { Unstable_Grid2 as Grid } from '@mui/material';

const FilterChips: FC = () => {
  const { t } = useTranslation();
  const { state, dispatch } = useSearchESContext();
  const { selectedGroups, selectedUsers } = useSearchFilter();

  const { post_filter } = state;
  const isEmpty = isEqual(state.post_filter, initialPostFilter);

  const onDeleteGroupChip = (group: UserFeedFollowGroupFragment) => {
    const updatedGroups = post_filter.group_list.filter(id => group.id !== id);
    dispatch({ type: 'setPostFilter', value: { group_list: updatedGroups } });
  };

  const onDeleteUserChip = (user: PublicUserProfileFragment) => {
    const updatedUsers = post_filter.submitter_list.filter(id => user.id !== id);
    dispatch({ type: 'setPostFilter', value: { submitter_list: updatedUsers } });
  };

  const onDeleteShareStatusChip = () =>
    dispatch({ type: 'setPostFilter', value: { share_status: PostShareStatus.AllPosts } });

  const onDeletePostTypeChip = (contentType: string) => {
    const updatedList = post_filter.content_types.filter(type => type !== contentType);
    dispatch({ type: 'setPostFilter', value: { content_types: updatedList } });
  };

  const onDeleteShareableChip = () =>
    dispatch({ type: 'setPostFilter', value: { is_shareable: false } });

  const onDeleteBrandedChip = () =>
    dispatch({ type: 'setPostFilter', value: { is_branded: false } });

  const onDeleteEngageableChip = () =>
    dispatch({ type: 'setPostFilter', value: { is_engageable: false } });

  const onDeleteUserGeneratedChip = () =>
    dispatch({ type: 'setPostFilter', value: { source: null } });

  function renderGroupChips() {
    if (!post_filter.group_list.length) {
      return null;
    }

    const groupList = compact(
      post_filter.group_list.map(id => selectedGroups.find(group => group.id === id))
    );

    return groupList.map(group => (
      <Grid key={group.id}>
        <FilterChip
          label={group.title}
          avatar={<GroupAvatar group={group} size={24} />}
          onDelete={() => onDeleteGroupChip(group)}
        />
      </Grid>
    ));
  }

  function renderUserChips() {
    if (!post_filter.submitter_list.length) {
      return null;
    }

    const userList = compact(
      post_filter.submitter_list.map(id => selectedUsers.find(user => user.id === id))
    );

    return userList.map(user => (
      <Grid key={user.id}>
        <FilterChip
          label={user.full_name}
          onDelete={() => onDeleteUserChip(user)}
          avatar={<UserAvatar user={user} size={24} />}
        />
      </Grid>
    ));
  }

  function renderPostTypeChips() {
    if (!post_filter.content_types.length) {
      return null;
    }

    return post_filter.content_types.map(contentType => (
      <Grid key={contentType}>
        <FilterChip label={contentType} onDelete={() => onDeletePostTypeChip(contentType)} />
      </Grid>
    ));
  }

  function renderShareStatusChips() {
    if (post_filter.share_status === PostShareStatus.AllPosts) {
      return null;
    }

    const label =
      post_filter.share_status === PostShareStatus.AlreadyShared
        ? t('search:alreadyShared')
        : t('search:notShared');

    return (
      <Grid>
        <FilterChip label={label} onDelete={onDeleteShareStatusChip} />
      </Grid>
    );
  }

  function renderShareableChips() {
    if (!post_filter.is_shareable) {
      return null;
    }

    return (
      <Grid>
        <FilterChip label={t('common:shareable')} onDelete={onDeleteShareableChip} />
      </Grid>
    );
  }

  function renderBrandedChips() {
    if (!post_filter.is_branded) {
      return null;
    }

    return (
      <Grid>
        <FilterChip label={t('common:branded')} onDelete={onDeleteBrandedChip} />
      </Grid>
    );
  }

  function renderEngageableChips() {
    if (!post_filter.is_engageable) {
      return null;
    }

    return (
      <Grid>
        <FilterChip label={t('posting:engageable')} onDelete={onDeleteEngageableChip} />
      </Grid>
    );
  }

  function renderUserGeneratedChips() {
    if (!post_filter.source) {
      return null;
    }

    return (
      <Grid>
        <FilterChip label={t('search:userGenerated')} onDelete={onDeleteUserGeneratedChip} />
      </Grid>
    );
  }

  if (isEmpty) {
    return null;
  }

  return (
    <Grid container spacing={1} marginTop={1}>
      {renderGroupChips()}
      {renderUserChips()}
      {renderPostTypeChips()}
      {renderShareStatusChips()}
      {renderBrandedChips()}
      {renderShareableChips()}
      {renderEngageableChips()}
      {renderUserGeneratedChips()}
    </Grid>
  );
};

export default FilterChips;
